import {
  Children,
  Component,
  isValidElement,
  useEffect,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { Sentry } from '@/lib/sentry';

/**
 * `<SeoHead>` — a page's `<title>` / `<meta>` / `<link>` block, isolated behind its own error
 * boundary.
 *
 * react-helmet-async 3.x renders these children natively under React 19 and lets React hoist
 * them into `<head>`. That hoisting path can throw React's internal invariant #446
 * (`"resourceRoot" was expected to exist. This is a bug in React.` — BOOKEPER-1CN, one Firefox
 * 140 visitor on www.bookeper.pl/, 2026-08-21). With the only boundary at the app root, a
 * failure inside a metadata block replaced the WHOLE landing page with the crash fallback.
 * Metadata is not worth the page: on failure this renders nothing for the head block, keeps
 * the page, and reports the error once under a stable fingerprint so it stays visible.
 *
 * The boundary is keyed on the route pathname: `/`, `/en` and `/uk` render the same page
 * component at the same tree position, so a locale switch would not remount a latched
 * boundary — keying it retries the head block on every navigation instead of leaving it blank
 * until a full reload.
 */
interface HeadBoundaryState {
  failed: boolean;
}

interface FallbackLink {
  rel: 'canonical' | 'alternate';
  href: string;
  hrefLang?: string;
}

interface FallbackMeta {
  attributes: Record<string, string>;
}

interface FallbackMetadata {
  links: FallbackLink[];
  title?: string;
  lang?: string;
  metas: FallbackMeta[];
}

interface HeadBoundaryProps {
  children: ReactNode;
  fallbackMetadata?: FallbackMetadata;
}

export class HeadBoundary extends Component<HeadBoundaryProps, HeadBoundaryState> {
  state: HeadBoundaryState = { failed: false };

  static getDerivedStateFromError(): HeadBoundaryState {
    return { failed: true };
  }

  private restoreFallbackMetadata() {
    const fallback = this.props.fallbackMetadata;
    if (fallback && typeof document !== 'undefined') {
      document.head.querySelectorAll('[data-landing-seo-fallback]').forEach((tag) => tag.remove());
      for (const metadata of fallback.links) {
        const link = document.createElement('link');
        link.rel = metadata.rel;
        link.href = metadata.href;
        if (metadata.hrefLang) link.hreflang = metadata.hrefLang;
        link.setAttribute('data-landing-seo-fallback', '');
        document.head.append(link);
      }
      if (fallback.lang) document.documentElement.lang = fallback.lang;
      if (fallback.title !== undefined) {
        document.head.querySelectorAll('title').forEach((title) => title.remove());
        const title = document.createElement('title');
        title.textContent = fallback.title;
        title.setAttribute('data-landing-seo-fallback', '');
        document.head.append(title);
      }
      for (const { attributes } of fallback.metas) {
        const identity = ['name', 'property', 'http-equiv', 'charset'].find(
          (attribute) => attributes[attribute] !== undefined
        );
        if (!identity) continue;
        document.head.querySelectorAll('meta').forEach((meta) => {
          if (meta.getAttribute(identity) === attributes[identity]) meta.remove();
        });
        const meta = document.createElement('meta');
        for (const [attribute, value] of Object.entries(attributes))
          meta.setAttribute(attribute, value);
        meta.setAttribute('data-landing-seo-fallback', '');
        document.head.append(meta);
      }
    }
  }

  componentDidUpdate(previousProps: HeadBoundaryProps) {
    // Locale providers can settle after a route's first failed render. Keep the
    // surviving metadata aligned with the page even while the boundary is latched.
    if (this.state.failed && this.props.fallbackMetadata !== previousProps.fallbackMetadata) {
      this.restoreFallbackMetadata();
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.restoreFallbackMetadata();
    Sentry.withScope((scope) => {
      scope.setFingerprint(['landing', 'seo-head-render-failure']);
      scope.setTag('landing.seo_head', 'failed');
      scope.setContext('seo_head', { componentStack: info.componentStack ?? '' });
      Sentry.captureException(error);
    });
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function readDeclaredMetadata(children: ReactNode): FallbackMetadata {
  const declaredElements = Children.toArray(children).filter(
    isValidElement<Record<string, unknown>>
  );
  const declaredTitle = declaredElements.find((child) => child.type === 'title')?.props.children;
  const declaredLang = declaredElements.find((child) => child.type === 'html')?.props.lang;
  const metas = declaredElements
    .filter((child) => child.type === 'meta')
    .map((child): FallbackMeta => ({
      attributes: Object.fromEntries(
        ['name', 'property', 'httpEquiv', 'charSet', 'content', 'media'].flatMap((attribute) => {
          const value = child.props[attribute];
          const htmlAttribute = attribute === 'httpEquiv' ? 'http-equiv' : attribute.toLowerCase();
          return typeof value === 'string' ? [[htmlAttribute, value]] : [];
        })
      ),
    }));
  const declaredLinks = Children.toArray(children).flatMap((child): FallbackLink[] => {
    if (
      !isValidElement<{ rel?: string; href?: string; hrefLang?: string }>(child) ||
      child.type !== 'link'
    )
      return [];
    const { rel, href, hrefLang } = child.props;
    if ((rel !== 'canonical' && rel !== 'alternate') || typeof href !== 'string') return [];
    if (rel === 'alternate' && !hrefLang) return [];
    return [{ rel, href, hrefLang }];
  });
  return {
    links: declaredLinks,
    title:
      typeof declaredTitle === 'string' || typeof declaredTitle === 'number'
        ? String(declaredTitle)
        : undefined,
    lang: typeof declaredLang === 'string' ? declaredLang : undefined,
    metas,
  };
}

function RoutedHeadBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const metadata = readDeclaredMetadata(children);
  const hasCanonical = metadata.links.some((link) => link.rel === 'canonical');
  const canonicalHref = `https://www.bookeper.pl${pathname}`;
  const fallbackLinks: FallbackLink[] = hasCanonical
    ? metadata.links
    : [{ rel: 'canonical', href: canonicalHref }, ...metadata.links];
  return (
    <HeadBoundary
      key={pathname}
      fallbackMetadata={{
        ...metadata,
        links: fallbackLinks,
      }}
    >
      <Helmet>
        {children}
        {!hasCanonical && <link rel="canonical" href={canonicalHref} />}
      </Helmet>
      <RemoveStaticSeoFallback metadata={metadata} />
    </HeadBoundary>
  );
}

function RemoveStaticSeoFallback({ metadata }: { metadata: FallbackMetadata }) {
  useEffect(() => {
    // Retire shell and failed-route metadata only after the new head succeeds.
    document.head.querySelectorAll('[data-landing-seo-fallback]').forEach((tag) => tag.remove());
    if (metadata.title !== undefined) {
      document.head
        .querySelectorAll('title[data-landing-seo-shell]')
        .forEach((tag) => tag.remove());
    }
    for (const { attributes } of metadata.metas) {
      const identity = ['name', 'property', 'http-equiv', 'charset'].find(
        (attribute) => attributes[attribute] !== undefined
      );
      if (!identity) continue;
      document.head.querySelectorAll('meta[data-landing-seo-shell]').forEach((meta) => {
        if (meta.getAttribute(identity) === attributes[identity]) meta.remove();
      });
    }
  }, [metadata]);
  return null;
}

export function SeoHead({ children }: { children: ReactNode }) {
  const inRouter = useInRouterContext();
  if (inRouter) return <RoutedHeadBoundary>{children}</RoutedHeadBoundary>;
  const metadata = readDeclaredMetadata(children);
  return (
    <HeadBoundary fallbackMetadata={metadata}>
      <Helmet>{children}</Helmet>
      <RemoveStaticSeoFallback metadata={metadata} />
    </HeadBoundary>
  );
}
